import { useState, useEffect, useCallback } from 'react';
import { Search, Shield, CheckCircle2, XCircle, Clock, MapPin, Hash, ChevronLeft, ChevronRight, Loader2, FileText, Camera, AlertTriangle, Smartphone } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { formatDateTime, formatTimeFull } from '@/lib/dateUtils';

function formatTS(ts) {
  return formatDateTime(ts);
}

export default function AdminAuditTrail() {
  const [search, setSearch] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const fetchTrail = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 50 };
      if (selectedEmp) params.employee_id = selectedEmp;
      else if (search.trim()) params.search = search.trim();
      if (actionFilter) params.action = actionFilter;
      if (dateFilter) params.date = dateFilter;
      const res = await adminAPI.getAuditTrail(params);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      setEmployees(res.data.employees || []);
      setPage(p);
      setHasSearched(true);
    } catch {
      toast.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  }, [search, selectedEmp, actionFilter, dateFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSelectedEmp(null);
    fetchTrail(1);
  };

  const selectEmployee = (empId) => {
    setSelectedEmp(empId);
    setSearch('');
  };

  useEffect(() => {
    if (selectedEmp) fetchTrail(1);
  }, [selectedEmp, actionFilter, dateFilter]);

  const selEmpName = employees.find(e => e.id === selectedEmp)?.name;

  return (
    <AdminLayout>
      <div className="space-y-5 animate-fade-in">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#026536]/10 flex items-center justify-center">
              <Shield className="text-[#026536]" size={20} />
            </div>
            <div>
              <h1 data-testid="audit-trail-title" className="text-2xl font-extrabold text-[#1a2529] tracking-tight">Audit Trail</h1>
              <p className="text-sm text-[#1a2529]/50">Immutable record of all check-in/check-out attempts</p>
            </div>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="bg-white rounded-xl border border-border/30 p-4 shadow-sm space-y-3">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1a2529]/30" />
              <Input data-testid="audit-search" value={search} onChange={e => { setSearch(e.target.value); setSelectedEmp(null); }}
                placeholder="Search by employee name or phone..." className="pl-10 h-10" />
            </div>
            <Button data-testid="audit-search-btn" type="submit" className="h-10 bg-[#026536] hover:bg-[#026536]/90 font-medium px-6"
              disabled={loading}>
              {loading ? <Loader2 className="animate-spin" size={16} /> : 'Search'}
            </Button>
          </form>

          <div className="flex flex-wrap gap-2">
            <select data-testid="action-filter" value={actionFilter} onChange={e => setActionFilter(e.target.value)}
              className="h-9 px-3 rounded-lg border border-border/40 text-sm text-[#1a2529] bg-white">
              <option value="">All Actions</option>
              <option value="CHECKIN">Check-in</option>
              <option value="CHECKOUT">Checkout</option>
              <option value="CHECKIN_FAILED">Failed Check-in</option>
            </select>
            <Input data-testid="date-filter" type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)}
              className="h-9 w-40 text-sm" />
            {(actionFilter || dateFilter || selectedEmp) && (
              <button data-testid="clear-filters" onClick={() => { setActionFilter(''); setDateFilter(''); setSelectedEmp(null); setEntries([]); setHasSearched(false); }}
                className="h-9 px-3 text-xs text-red-500 font-medium hover:underline">Clear Filters</button>
            )}
          </div>

          {/* Selected employee chip */}
          {selectedEmp && selEmpName && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#1a2529]/50">Showing trail for:</span>
              <Badge data-testid="selected-employee-badge" className="bg-[#026536]/10 text-[#026536] border-0 font-semibold text-sm px-3 py-1">
                {selEmpName}
                <button onClick={() => { setSelectedEmp(null); setEntries([]); setHasSearched(false); }} className="ml-2 text-[#026536]/50 hover:text-[#026536]">&times;</button>
              </Badge>
            </div>
          )}

          {/* Employee matches (if search returned multiple) */}
          {!selectedEmp && employees.length > 1 && hasSearched && (
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-[#1a2529]/40 self-center">Select employee:</span>
              {employees.map(emp => (
                <button key={emp.id} data-testid={`emp-chip-${emp.phone}`} onClick={() => selectEmployee(emp.id)}
                  className="text-xs px-3 py-1.5 rounded-full bg-[#f4f4f5] hover:bg-[#026536]/10 text-[#1a2529]/70 font-medium transition-colors">
                  {emp.name} <span className="text-[#1a2529]/30">({emp.phone})</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results */}
        {!hasSearched ? (
          <div className="bg-white rounded-xl border border-border/30 p-12 text-center">
            <Shield className="mx-auto text-[#1a2529]/10 mb-3" size={48} />
            <p className="text-[#1a2529]/50 font-medium">Search for an employee to view their audit trail</p>
            <p className="text-xs text-[#1a2529]/30 mt-1">Every check-in/check-out attempt is permanently recorded</p>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#026536]" size={28} /></div>
        ) : entries.length === 0 ? (
          <div className="bg-white rounded-xl border border-border/30 p-8 text-center">
            <FileText className="mx-auto text-[#1a2529]/10 mb-3" size={40} />
            <p className="text-[#1a2529]/50 font-medium">No audit entries found</p>
            <p className="text-xs text-[#1a2529]/30 mt-1">
              {search ? `No records for "${search}"` : 'Try a different search or filter'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-[#1a2529]/40">{total} entries found</p>

            <div className="space-y-2">
              {entries.map((e, idx) => (
                <div key={e.id || idx} data-testid={`audit-entry-${idx}`}
                  className={`bg-white rounded-xl border p-4 transition-colors ${
                    e.status === 'failed' ? 'border-red-200 bg-red-50/30' : 'border-border/30'
                  }`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      {/* Status icon */}
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                        e.status === 'failed' ? 'bg-red-100' :
                        e.action === 'CHECKOUT' ? 'bg-amber-100' :
                        'bg-emerald-100'
                      }`}>
                        {e.status === 'failed' ? <XCircle size={18} className="text-red-500" /> :
                         e.action === 'CHECKOUT' ? <Clock size={18} className="text-amber-600" /> :
                         <CheckCircle2 size={18} className="text-emerald-600" />}
                      </div>

                      <div className="min-w-0">
                        {/* Employee name + action */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm text-[#1a2529]">{e.user_name}</p>
                          <Badge className={`text-[10px] font-bold border-0 ${
                            e.status === 'failed' ? 'bg-red-100 text-red-600' :
                            e.action === 'CHECKOUT' ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {e.action?.replace('_', ' ')}
                          </Badge>
                          {e.source === 'ledger' && (
                            <Badge className="bg-blue-50 text-blue-600 border-0 text-[9px]">LEDGER</Badge>
                          )}
                          {e.source === 'audit_log' && (
                            <Badge className="bg-[#f4f4f5] text-[#1a2529]/40 border-0 text-[9px]">AUDIT LOG</Badge>
                          )}
                        </div>

                        {/* Phone */}
                        {e.user_phone && (
                          <p className="text-xs text-[#1a2529]/40 mt-0.5">+91 {e.user_phone}</p>
                        )}

                        {/* Error */}
                        {e.error && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <AlertTriangle size={12} className="text-red-400 shrink-0" />
                            <p className="text-xs text-red-500 font-medium">{e.error}</p>
                          </div>
                        )}

                        {/* Location */}
                        {(e.latitude || e.address) && (
                          <div className="flex items-start gap-1.5 mt-1.5">
                            <MapPin size={12} className="text-[#1a2529]/30 mt-0.5 shrink-0" />
                            <div className="text-xs text-[#1a2529]/50">
                              {e.address && <span>{e.address}</span>}
                              {e.latitude && <span className="font-mono text-[10px] ml-1">({e.latitude?.toFixed(5)}, {e.longitude?.toFixed(5)})</span>}
                            </div>
                          </div>
                        )}

                        {/* Selfie proof */}
                        {e.selfie_hash && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <Camera size={11} className="text-[#1a2529]/25" />
                            <span className="text-[10px] font-mono text-[#1a2529]/30">
                              Selfie: {e.selfie_hash} ({e.selfie_size ? `${(e.selfie_size / 1024).toFixed(0)}KB` : '-'})
                            </span>
                          </div>
                        )}

                        {/* Audit log details */}
                        {e.details && !e.address && (
                          <p className="text-xs text-[#1a2529]/40 mt-1">{e.details}</p>
                        )}
                      </div>
                    </div>

                    {/* Timestamp + IDs */}
                    <div className="text-right shrink-0">
                      <div className="space-y-0.5">
                        <p className="text-xs font-medium text-[#1a2529]/70">{formatTS(e.timestamp)}</p>
                        <p className="text-[10px] text-[#1a2529]/25">Server time</p>
                      </div>
                      {e.device_timestamp && (
                        <div className="mt-1.5 pt-1.5 border-t border-dashed border-[#1a2529]/10 space-y-0.5">
                          <div className="flex items-center gap-1 justify-end">
                            <Smartphone size={10} className="text-blue-400" />
                            <p className="text-xs font-medium text-blue-600">{formatTimeFull(e.device_timestamp)}</p>
                          </div>
                          <p className="text-[10px] text-blue-400">Device time</p>
                        </div>
                      )}
                      {e.date && <p className="text-[10px] text-[#1a2529]/30 mt-1">{e.date.split('-').reverse().join('/')}</p>}
                      {e.attendance_id && (
                        <div className="flex items-center gap-1 justify-end mt-1">
                          <Hash size={10} className="text-[#1a2529]/20" />
                          <span className="text-[10px] font-mono text-[#1a2529]/25 select-all">{e.attendance_id.slice(0, 8).toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {total > 50 && (
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => fetchTrail(page - 1)} className="h-8 text-xs">
                  <ChevronLeft size={14} /> Prev
                </Button>
                <span className="text-xs text-[#1a2529]/50">Page {page}</span>
                <Button variant="outline" size="sm" disabled={entries.length < 50}
                  onClick={() => fetchTrail(page + 1)} className="h-8 text-xs">
                  Next <ChevronRight size={14} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
